import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { PageChangeEvent } from '@app/core/components/pager/pager.component';
import { FileInfo, FileService } from '@app/core/services/file.service';
import { RememberService } from '@app/core/services/remember.service';
import { DocumentItem, DocumentService } from '@app/services/document.service';
import { PicturesGet } from '@app/services/picture.service';
import { PictureCardItem } from '../picture-card/picture-card.component';

@Component({
    selector: 'tt-pictures-list',
    templateUrl: './pictures-list.component.html',
    styleUrls: ['./pictures-list.component.css'],
    standalone: false,
})
export class PicturesListComponent implements OnChanges, OnDestroy {
    @Input()
    public page!: PicturesGet;

    public pictures: PictureCardItem[] = [];

    public skeletons: number[] = [];

    private loadedPageIndex?: number;

    private loadedPageSize?: number;

    private loadId = 0;

    constructor(
        private document: DocumentService,
        private file: FileService,
        private remember: RememberService,
    ) {}

    public reload(): Promise<void> {
        return this.loadPictures(this.currentPageEvent());
    }

    public async loadPictures(params: PageChangeEvent): Promise<void> {
        const loadId = ++this.loadId;
        const expectedPictureCount = this.getExpectedPictureCount(params);

        if (this.reuseLoadedRange(params, expectedPictureCount)) return;

        const previousById = this.getPicturesToPreserve(params);
        if (previousById.size === 0) this.pictures = [];
        this.showSkeletons(expectedPictureCount);

        try {
            const documents = await this.getDocuments(params);
            if (!this.isCurrentLoad(loadId)) return;
            this.updateTotal(documents);

            const filesByName = await this.loadMissingFileInfo(documents, previousById);
            if (!this.isCurrentLoad(loadId)) return;

            this.pictures = this.mergePictures(documents, previousById, filesByName);
            this.setLoadedRange(params);
        } catch (error) {
            if (this.isCurrentLoad(loadId)) console.error(error);
        } finally {
            if (this.isCurrentLoad(loadId)) this.skeletons = [];
        }
    }

    public onPageChange(params: PageChangeEvent): void {
        this.page.pageindex = `${params.pageIndex}`;
        this.page.pagesize = `${params.pageSize}`;
        void this.loadPictures(params);
        void Promise.all([
            this.remember.remember('w_pictures.pagesize', `${params.pageSize}`),
            this.remember.remember('w_pictures.pageindex', `${params.pageIndex}`),
        ]).catch((error) => console.error(error));
    }

    public trackByPicture(_index: number, picture: PictureCardItem): string {
        return picture.item_id;
    }

    private initialize(): void {
        ++this.loadId;
        this.pictures = [];
        this.skeletons = [];
        this.loadedPageIndex = undefined;
        this.loadedPageSize = undefined;
        this.normalizePage();
        void this.reload();
    }

    private normalizePage(): void {
        const pageSize = this.validInteger(this.page.pagesize, 25, 1);
        const total = this.validInteger(this.page.total, 0, 0);
        const requestedPageIndex = this.validInteger(this.page.pageindex, 0, 0);
        const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1);

        this.page.pagesize = `${pageSize}`;
        this.page.total = `${total}`;
        this.page.pageindex = `${Math.min(requestedPageIndex, lastPageIndex)}`;
    }

    private validInteger(value: `${number}`, fallback: number, minimum: number): number {
        const number = Number(value);
        return Number.isInteger(number) && number >= minimum ? number : fallback;
    }

    private currentPageEvent(): PageChangeEvent {
        return {
            pageIndex: Number(this.page.pageindex),
            pageSize: Number(this.page.pagesize),
            totalCount: Number(this.page.total),
        };
    }

    private getExpectedPictureCount(params: PageChangeEvent): number {
        const total = Number(this.page.total);
        if (total === 0) return params.pageSize;

        const requestedStart = params.pageIndex * params.pageSize;
        return Math.max(0, Math.min(params.pageSize, total - requestedStart));
    }

    private reuseLoadedRange(params: PageChangeEvent, expectedPictureCount: number): boolean {
        if (this.loadedPageIndex === undefined || this.loadedPageSize === undefined || params.pageSize >= this.loadedPageSize) return false;

        const requestedStart = params.pageIndex * params.pageSize;
        const loadedStart = this.loadedPageIndex * this.loadedPageSize;
        const offset = requestedStart - loadedStart;
        if (offset < 0 || offset + expectedPictureCount > this.pictures.length) return false;

        this.pictures = this.pictures.slice(offset, offset + expectedPictureCount);
        this.skeletons = [];
        this.setLoadedRange(params);
        return true;
    }

    private getPicturesToPreserve(params: PageChangeEvent): Map<string, PictureCardItem> {
        const pageSizeIncreasedOnFirstPage = params.pageIndex === 0 && this.loadedPageIndex === 0 && this.loadedPageSize !== undefined && params.pageSize > this.loadedPageSize;
        return new Map((pageSizeIncreasedOnFirstPage ? this.pictures : []).map((picture) => [picture.item_id, picture]));
    }

    private showSkeletons(expectedPictureCount: number): void {
        const missingPictureCount = Math.max(0, expectedPictureCount - this.pictures.length);
        this.skeletons = Array.from({ length: missingPictureCount }, (_, index) => index);
    }

    private isCurrentLoad(loadId: number): boolean {
        return loadId === this.loadId;
    }

    private updateTotal(documents: DocumentItem[]): void {
        const total = documents[0]?.total_nbr_of_rows;
        if (total !== undefined) this.page.total = total;
    }

    private async loadMissingFileInfo(documents: DocumentItem[], previousById: Map<string, PictureCardItem>): Promise<Map<string, FileInfo>> {
        const filenames = documents
            .filter((document) => {
                const previous = previousById.get(document.item_id);
                return !previous?.url || previous.docfilename !== document.docfilename;
            })
            .map((document) => document.docfilename);

        if (filenames.length === 0) return new Map();

        const { files } = await this.file.getFileInfo({ files: filenames, selectOnlyFromFiles: true });
        return new Map(files.map((file) => [file.fileName, file]));
    }

    private mergePictures(documents: DocumentItem[], previousById: Map<string, PictureCardItem>, filesByName: Map<string, FileInfo>): PictureCardItem[] {
        return documents.map((document) => {
            const previous = previousById.get(document.item_id);
            if (previous?.url && previous.docfilename === document.docfilename) {
                Object.assign(previous, document);
                return previous;
            }

            return { ...document, ...filesByName.get(document.docfilename) };
        });
    }

    private setLoadedRange(params: PageChangeEvent): void {
        this.loadedPageIndex = params.pageIndex;
        this.loadedPageSize = params.pageSize;
    }

    private getDocuments(params: PageChangeEvent): Promise<DocumentItem[]> {
        const { total, pagesize, pageindex, view, ...documentPage } = this.page;
        return this.document.getDocuments({ ...documentPage, searchtext: '', doctype: 'p', onlyFilenames: '0', page_no: params.pageIndex + 1, max_nbr_of_rows: params.pageSize });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['page']?.currentValue) this.initialize();
    }

    ngOnDestroy(): void {
        ++this.loadId;
    }
}
